import React, { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Section } from "@/components/section";
import { mutateRequest, postFetcher, putFetcherKeyed } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { Chip } from "@/components/badge/chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { App } from "@/types/domain";
import { refreshApps } from "./refresh-apps";
import { NotifyEventPrefs } from "./notify-event-prefs";

// Feishu notify-group binding (owner/admin): paste the chat_id the wed app
// posts into a group when invited (a full group picker would leak every
// group name org-wide). The server verifies the wed app really is in the
// pasted group. Legacy webhook bots (migrated from config) are shown
// read-only; chatId wins when both exist.
export const NotifyChatSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  const [chatIdInput, setChatIdInput] = useState("");
  // Only the bound group's name is exposed (null name = app not in it anymore).
  const { data: chatInfo } = useSWR<{
    chat_id: string;
    name: string | null;
  } | null>(app.chatId ? [`/apps/${app.id}/notify/chat`] : null);
  const { trigger: bind, isMutating } = useSWRMutation(
    `/apps/${app.id}/notify/chat#bind`,
    putFetcherKeyed,
    {
      onSuccess: () => {
        refreshApps();
        mutateRequest(`/apps/${app.id}/notify/chat`);
        setChatIdInput("");
      },
      onError: (err: unknown) =>
        toastError(toast, "Bind failed", err),
    }
  );
  // One-click invite of the app's owner + visible members into the bound
  // group (server skips people already in it).
  const { trigger: invite, isMutating: inviting } = useSWRMutation(
    `/apps/${app.id}/notify/chat/invite-members`,
    postFetcher,
    {
      onSuccess: (res: { requested: number; invalid?: number }) =>
        toast({
          title: "Invited",
          description: `${res.requested} requested${
            res.invalid ? `, ${res.invalid} skipped (invalid)` : ""
          }.`,
        }),
      onError: (err: unknown) =>
        toastError(toast, "Invite failed", err),
    }
  );
  // No existing group to bind: let wed create one (owner + members join
  // automatically) and bind it in one go.
  const { trigger: createGroup, isMutating: creating } = useSWRMutation(
    `/apps/${app.id}/notify/chat/create`,
    postFetcher,
    {
      onSuccess: (res: { invited: number }) => {
        refreshApps();
        mutateRequest(`/apps/${app.id}/notify/chat`);
        toast({
          title: "Group created",
          description: `${res.invited} member(s) invited.`,
        });
      },
      onError: (err: unknown) =>
        toastError(toast, "Create failed", err),
    }
  );

  return (
    <Section title="Notifications">
      <div className="flex flex-col gap-2">
        {app.chatId ? (
          <div className="flex items-center gap-2 text-sm">
            <Chip tone="green">Bound</Chip>
            <span className="truncate">
              {chatInfo?.name ?? app.chatId}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={inviting}
              onClick={() => invite({})}
            >
              {inviting ? "Inviting…" : "Invite owner & members"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              disabled={isMutating}
              onClick={() => bind({ chatId: null })}
            >
              Unbind
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              value={chatIdInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setChatIdInput(e.target.value)
              }
              placeholder="Paste chat_id (oc_…)"
              className="h-8 w-72"
            />
            <Button
              size="sm"
              className="h-8"
              disabled={isMutating || !chatIdInput.trim().startsWith("oc_")}
              onClick={() => bind({ chatId: chatIdInput.trim() })}
            >
              Bind
            </Button>
            <span className="text-xs text-muted-foreground">or</span>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={creating}
              onClick={() => createGroup({})}
            >
              {creating ? "Creating…" : "Create group"}
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          App activity (versions, reports, bugs, PRD scheduling) is sent to
          the bound group. Bind an existing group (invite the wed Feishu app
          into it — it replies with the chat_id to paste here), or let wed
          create a fresh group with the owner and members in it.
        </p>
        {!app.chatId && (app.webhookBots ?? []).length > 0 && (
          <p className="text-xs text-muted-foreground">
            Legacy webhook bot in use ({(app.webhookBots ?? []).length}) —
            binding a group switches delivery to it.
          </p>
        )}
        <NotifyEventPrefs app={app} />
      </div>
    </Section>
  );
};
